
import { computed, defineComponent, PropType, ref } from 'vue'
import { saveSettings } from '../api'
import {
    catalogPriceLabel,
    catalogTypeName,
    formatDuration,
    passesMinPrice,
    plural,
    sortCatalog,
} from '../helpers'
import { CatalogService, HotWindowsFilters } from '../types'
import HwIcon from './HwIcon.vue'

// Сумма, которую подставляем в поле, пока порог не задан, — как в макете.
const DEFAULT_PRICE = 1000

/**
 * Один каталог в двух режимах: settings — постоянный набор услуг проекта
 * (serviceList в настройках), filter — временный фильтр экрана внутри
 * этого набора плюс порог цены.
 */
export default defineComponent({
    name: 'HotWindowsServicesDrawer',
    components: { HwIcon },
    props: {
        mode: { type: String as PropType<'settings' | 'filter'>, required: true },
        catalog: { type: Array as PropType<CatalogService[]>, required: true },
        allowed: { type: Array as PropType<string[]>, required: true },
        filters: { type: Object as PropType<HotWindowsFilters>, required: true },
        projectId: { type: String, required: true },
    },
    emits: ['close', 'saved', 'apply', 'toast'],
    setup(props, { emit }) {
        const isSettings = computed(() => props.mode === 'settings')
        const draft = ref<string[]>(isSettings.value ? [...props.allowed] : [...props.filters.serviceIds])
        const search = ref('')
        const category = ref('all')
        const saving = ref(false)

        const draftMinPrice = ref(props.filters.minPrice)
        const priceValid = ref(true)
        const priceText = ref(String(props.filters.minPrice > 0 ? props.filters.minPrice : DEFAULT_PRICE))

        // Порог включается, только когда админ трогает поле: сама подсказка
        // «1000» фильтром не считается — так же, как в макете.
        function updatePrice() {
            const text = String(priceText.value ?? '').trim()
            const value = text === '' ? 0 : Number(text)
            priceValid.value = Number.isSafeInteger(value) && value >= 0
            if (priceValid.value) draftMinPrice.value = value
        }
        function clearPrice() {
            draftMinPrice.value = 0
            priceValid.value = true
        }

        const allowedSet = computed(() => new Set(props.allowed))
        const withinSet = computed(() =>
            isSettings.value ? props.catalog : props.catalog.filter(service => allowedSet.value.has(service.id))
        )
        const categories = computed(() =>
            [...new Set(withinSet.value.map(service => service.category))].sort((a, b) => a.localeCompare(b, 'ru'))
        )
        const visible = computed(() => {
            const query = search.value.trim().toLowerCase()
            return sortCatalog(
                withinSet.value.filter(
                    service =>
                        (category.value === 'all' || service.category === category.value) &&
                        service.name.toLowerCase().includes(query)
                )
            )
        })

        const resultCount = computed(() => {
            if (!priceValid.value) return 'Исправьте минимальную стоимость, чтобы применить фильтр.'
            const picked = new Set(draft.value)
            const fits = withinSet.value.filter(
                service => (!picked.size || picked.has(service.id)) && passesMinPrice(service, draftMinPrice.value)
            ).length
            return `Во всём наборе: ${fits} ${plural(fits, 'услуга', 'услуги', 'услуг')} по условиям фильтра. Длительность и наличие кандидатов проверим для каждого окна.`
        })

        // «Выбрать все» — по видимым услугам: с поиском или категорией
        // отмечает только найденное, не трогая остальной выбор.
        const allVisibleSelected = computed(() => {
            const picked = new Set(draft.value)
            return visible.value.length > 0 && visible.value.every(service => picked.has(service.id))
        })
        function toggleVisible() {
            const ids = visible.value.map(service => service.id)
            if (allVisibleSelected.value) {
                const hide = new Set(ids)
                draft.value = draft.value.filter(id => !hide.has(id))
            } else {
                draft.value = [...new Set([...draft.value, ...ids])]
            }
        }

        const selectedLabel = computed(() => {
            if (draft.value.length) return `Выбрано: ${draft.value.length}`
            return isSettings.value ? 'Ни одна услуга не выбрана' : 'Все настроенные услуги'
        })

        async function save() {
            if (!isSettings.value) {
                if (!priceValid.value) return
                // Отмечен весь набор — это то же, что «все услуги»: фильтр по
                // услугам не передаём, иначе функция получит явный список и
                // снимет лимит топ-20 — расчёт станет заметно дольше.
                const picked = new Set(draft.value)
                const wholeSet = withinSet.value.every(service => picked.has(service.id))
                emit('apply', {
                    serviceIds: wholeSet ? [] : [...draft.value],
                    minPrice: draftMinPrice.value,
                })
                return
            }
            saving.value = true
            const { data, error } = await saveSettings({
                projectId: props.projectId,
                settings: { serviceList: [...draft.value] },
            })
            saving.value = false
            if (error || !data) {
                emit('toast', 'Не удалось сохранить набор услуг')
                return
            }
            emit('saved', data)
        }

        return {
            isSettings,
            draft,
            search,
            category,
            saving,
            draftMinPrice,
            priceValid,
            priceText,
            updatePrice,
            clearPrice,
            categories,
            visible,
            resultCount,
            selectedLabel,
            allVisibleSelected,
            toggleVisible,
            save,
            isBelowPrice: (service: CatalogService) =>
                !isSettings.value && priceValid.value && !passesMinPrice(service, draftMinPrice.value),
            meta: (service: CatalogService) =>
                `${catalogPriceLabel(service)} · ${formatDuration(service.durationMinutes)} · ${service.category}`,
            typeName: catalogTypeName,
        }
    },
})
